import { requireNonNullable } from '@disy/lib/custom-error';
import { getDefaultSetting, getSettingOrSettingDefault } from '@disy/workbook.lib/settings';

import type { StylePropertyValue } from 'cadenza/api-client/model/style-property-value';
import { Command } from 'cadenza/api-client/workbook-commands';
import { getStore } from 'cadenza/store/cadenza-store';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { getDataViewByActiveAttributeId } from 'cadenza/workbook/data-view/data-view-utils';
import type { AttributeBundleName } from 'cadenza/workbook/model/attribute-bundle-name';
import type { AttributeValueClassificationMode } from 'cadenza/workbook/model/attribute-value-classification-mode.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { IncludesLimit } from 'cadenza/workbook/model/includes-limit.gen';
import type { IntervalClassificationClassDto } from 'cadenza/workbook/model/interval-classification-model';
import { MapDataViewSettingsKey } from 'cadenza/workbook/model/map-data-view-settings-key.gen';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import { getAttributeById, getDataViewById } from 'cadenza/workbook/workbook-core-selectors.state';
import {
  CLASSIFICATION_CLASS_LABEL,
  INTERVAL_CLASSIFICATION_CLASS_BOUNDARIES,
} from 'cadenza/workbook/workbook-panel/settings/classification/interval-classification-settings-consts';

export function setManualIntervalClassificationClasses<T extends StylePropertyValue>(
  attributeId: ActiveAttributeId,
  attributeBundleName: AttributeBundleName,
  classificationClasses: IntervalClassificationClassDto<T>[],
  includesLimit: IncludesLimit = getIncludesLimit(attributeId),
) {
  const attribute = getAttributeById(getStore().getState(), attributeId);
  const command = Command.SET_DATA_VIEW_ATTRIBUTE_INTERVAL_CLASSIFICATION({
    dataViewId: requireNonNullable(getDataViewByActiveAttributeId(attributeId)).id,
    attributeBundleName,
    includesLimit,
    // We have a requirement saying if the label is the default one, we should not store it (it is considered 'AUTO')
    intervalClassificationClasses: classificationClasses.map((c) => ({
      ...c,
      label: c.label === getDefaultSetting(attribute, CLASSIFICATION_CLASS_LABEL) ? undefined : c.label,
    })),
  });
  return getStore().dispatch(executeCommand(command));
}

export function getIncludesLimit(attributeOrActiveAttributeId: WorkbookAttributeDto | ActiveAttributeId) {
  const attribute = getOrReturnAttribute(attributeOrActiveAttributeId);
  return IncludesLimit.fromPersistenceKey(
    getSettingOrSettingDefault(attribute, INTERVAL_CLASSIFICATION_CLASS_BOUNDARIES),
  );
}

export function getClassificationClassLabel(attributeOrActiveAttributeId: WorkbookAttributeDto | ActiveAttributeId) {
  const attribute = getOrReturnAttribute(attributeOrActiveAttributeId);
  return getSettingOrSettingDefault<string>(attribute, CLASSIFICATION_CLASS_LABEL);
}

function getOrReturnAttribute(attributeOrActiveAttributeId: WorkbookAttributeDto | ActiveAttributeId) {
  if (typeof attributeOrActiveAttributeId === 'string') {
    return getAttributeById(getStore().getState(), attributeOrActiveAttributeId);
  }
  return attributeOrActiveAttributeId;
}

export function getClassificationMode(dataViewId: DataViewId) {
  const dataViewById = getDataViewById(getStore().getState(), dataViewId);
  return getSettingOrSettingDefault<AttributeValueClassificationMode>(
    dataViewById,
    MapDataViewSettingsKey.toPersistenceKey('CLASSIFICATION_MODE'),
  );
}
